import React, { useEffect, useState } from 'react';

import { resolveApiHref } from '@/app/config/apiConfig';
import imgAlipay from '@/assets/icons/alipay-login.png';
import imgStarDark from '@/assets/icons/star-dark.png';
import imgStarLight from '@/assets/icons/star-light.png';
import imgWechat from '@/assets/icons/wechat-login.png';
import Modal from '@/components/common/Modal';
import type { PaymentMethod, PaymentProduct } from '@/types';

interface PointsBuyModalProps {
  open: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
  paymentProducts: PaymentProduct[];
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodId: string | null;
  onSelectPaymentMethod: (methodId: string) => void;
  paymentProductsLoading: boolean;
  purchasingProductId: string | null;
  paymentPurchaseError: string | null;
  onPurchaseProduct: (product: PaymentProduct, paymentMethodId: string) => void;
}

/** 根据支付方式名称推断图标：支付宝/微信有专属图标，其余不显示 */
function resolveMethodIcon(name: string): string | null {
  const n = name.toLowerCase();
  if (n.includes('alipay') || n.includes('支付宝')) return imgAlipay;
  if (n.includes('wechat') || n.includes('微信') || n.includes('weixin')) return imgWechat;
  return null;
}

function resolveMarketingLabelImage(product: PaymentProduct): string {
  if (!product.marketing_label_enabled) return '';
  const imageUrl = product.image_url?.trim() || '';
  return imageUrl ? resolveApiHref(imageUrl) : '';
}

const formatAmount = (amountCent: number, currency: string): string => {
  const symbol = currency === 'CNY' ? '¥' : `${currency} `;
  const amount = amountCent / 100;
  return `${symbol}${Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2)}`;
};

const PAYMENT_AGREEMENT_URL =
  'https://terms.alicdn.com/legal-agreement/terms/common_platform_service/20260708101854326/20260708101854326.html';
const PRIVACY_POLICY_URL =
  'https://terms.alicdn.com/legal-agreement/terms/privacy_policy_full/20260316193944132/20260316193944132.html';

const PointsBuyModal: React.FC<PointsBuyModalProps> = ({
  open,
  onClose,
  theme,
  paymentProducts,
  paymentMethods,
  selectedPaymentMethodId,
  onSelectPaymentMethod,
  paymentProductsLoading,
  purchasingProductId,
  paymentPurchaseError,
  onPurchaseProduct,
}) => {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementShake, setAgreementShake] = useState(false);

  // 默认选中第一个产品
  useEffect(() => {
    if (paymentProducts.length > 0 && !selectedProductId) {
      setSelectedProductId(paymentProducts[0].product_id);
    }
  }, [paymentProducts, selectedProductId]);

  // 弹窗关闭时重置选中
  useEffect(() => {
    if (!open) {
      setSelectedProductId(null);
      setAgreementAccepted(false);
      setAgreementShake(false);
    }
  }, [open]);

  const selectedProduct = paymentProducts.find((p) => p.product_id === selectedProductId) ?? null;
  const isPurchasing = purchasingProductId !== null;
  const gridCols = [1, 2, 4].includes(paymentProducts.length) ? 'grid-cols-2' : 'grid-cols-3';
  const canPurchase =
    selectedProduct !== null &&
    (paymentMethods.length === 0 || selectedPaymentMethodId !== null) &&
    !isPurchasing;

  const handlePurchaseClick = () => {
    if (!selectedProduct || !canPurchase) return;
    if (!agreementAccepted) {
      // 未勾选协议：重新触发协议区晃动提示，不进入下单流程
      setAgreementShake(false);
      requestAnimationFrame(() => setAgreementShake(true));
      return;
    }
    onPurchaseProduct(selectedProduct, selectedPaymentMethodId ?? '');
  };

  return (
    <>
      <Modal
        title="积分购买"
        description="选择积分套餐并完成购买"
        open={open}
        onClose={onClose}
        width="min(560px, calc(100vw - 48px))"
      >
        <div className="space-y-5">
          {paymentPurchaseError ? (
            <div className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {paymentPurchaseError}
            </div>
          ) : null}

          {paymentProductsLoading ? (
            <div className={`grid ${gridCols} gap-3`}>
              <div className="h-24 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
              <div className="h-24 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
              <div className="h-24 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
            </div>
          ) : (
            <div className={`grid ${gridCols} gap-3`}>
              {paymentProducts.map((product) => {
                const isSelected = selectedProductId === product.product_id;
                const marketingLabelImage = resolveMarketingLabelImage(product);
                return (
                  <button
                    key={product.product_id}
                    type="button"
                    onClick={() => setSelectedProductId(product.product_id)}
                    className={`relative flex flex-col items-center justify-center gap-1 overflow-visible rounded-lg border-2 px-3 py-4 transition-colors ${
                      isSelected
                        ? 'border-theme-color bg-theme-color-10'
                        : 'border-dark-border bg-dark-card hover:border-theme-color/50'
                    }`}
                  >
                    {marketingLabelImage ? (
                      <span className="pointer-events-none absolute left-[-3px] top-0 -translate-y-[calc(50%+2px)]">
                        <img
                          src={marketingLabelImage}
                          alt="优惠标签"
                          className={`block h-[29px] max-w-[115px] origin-[5px_100%] object-contain ${
                            isSelected ? 'animate-tag-wiggle' : ''
                          }`}
                        />
                      </span>
                    ) : null}
                    <span className="flex items-center gap-1">
                      <img
                        src={theme === 'dark' ? imgStarDark : imgStarLight}
                        alt="积分"
                        className="h-4 w-4"
                      />
                      <span
                        className={`text-xl font-bold tabular-nums ${
                          isSelected ? 'text-theme-color' : 'text-dark-text'
                        }`}
                        style={{ fontFamily: 'AlibabaSans102-Bd' }}
                      >
                        {product.points}
                      </span>
                    </span>
                    <span className="text-sm text-dark-text-secondary tabular-nums">
                      {formatAmount(product.amount_cent, product.currency)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {paymentMethods.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-dark-text-secondary">支付方式</p>
              <div className="flex gap-3" role="group" aria-label="支付方式">
                {paymentMethods.map((method) => {
                  const isSelected = method.method_id === selectedPaymentMethodId;
                  const icon = resolveMethodIcon(method.name);
                  return (
                    <button
                      key={method.method_id}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => onSelectPaymentMethod(method.method_id)}
                      className={`flex flex-1 h-11 items-center justify-center gap-2 rounded-lg border-2 px-4 text-sm font-medium transition-colors ${
                        isSelected
                          ? 'border-theme-color bg-theme-color-10 text-dark-text'
                          : 'border-dark-border bg-dark-card text-dark-text hover:border-theme-color/50'
                      }`}
                    >
                      {icon ? (
                        <img
                          src={icon}
                          alt={method.name}
                          className="h-5 w-5 rounded-sm object-contain"
                        />
                      ) : null}
                      {method.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div
            onAnimationEnd={() => setAgreementShake(false)}
            className={`flex items-start gap-2 rounded-lg border bg-dark-card/70 px-3 py-2 text-xs leading-5 text-dark-text-secondary ${
              agreementShake ? 'animate-shake border-red-500/60' : 'border-dark-border/70'
            }`}
          >
            <input
              id="points-payment-agreement"
              type="checkbox"
              aria-label="同意ManClaw平台付费服务协议和ManClaw隐私政策"
              checked={agreementAccepted}
              onChange={(event) => setAgreementAccepted(event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-theme-color"
            />
            <div>
              <label htmlFor="points-payment-agreement" className="cursor-pointer">
                我已阅读并同意
              </label>
              <a
                href={PAYMENT_AGREEMENT_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-theme-color hover:text-theme-color/80"
              >
                《ManClaw平台付费服务协议》
              </a>
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-theme-color hover:text-theme-color/80"
              >
                《ManClaw隐私政策》
              </a>
            </div>
          </div>

          <button
            type="button"
            disabled={!canPurchase}
            onClick={() => handlePurchaseClick()}
            className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-theme-color text-base font-medium text-white transition-colors hover:bg-theme-color/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPurchasing ? '下单中...' : '购买'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default PointsBuyModal;
